import { DataTable } from "primereact/datatable";
import {
  CreditInvoicesSummaryColumns,
  rowExpansionCreditInvoicesSummaryLineItemColumns
} from "../../constants/table-columns/tableColumns";
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import Spinner from "../../components/spinner/spinner";
import {
  getEdi894FileContent,
  getInvoiceDetails
} from "../../services/creditInvoiceService";
import { Toast } from "primereact/toast";
import * as constants from "../../constants/constants";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  creditInvoiceStatusKeyValueOptions,
  creditInvoiceStatusOptions
} from "../../constants/constants";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import styles from "./CreditInvoiceSummary.module.scss";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionTypes from "../../store/actions";
import * as actionCreators from "../../store/action-creators";
import { sortData } from "../../constants/dataSort";
import { appInsights } from "../../AppInsights";
import LoginUserDataContext from "../../store/login";

export default function CreditInvoiceSummary() {
  const toast = useRef(null);
  const formRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const [expandedRows, setExpandedRows] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [userData, setUserData] = useContext(LoginUserDataContext);
  const [showSpinner, setShowSpinner] = useState(false);
  const [disableFields, setDisableFields] = useState(false);
  const [ediFileContentHeaderTitle, setEdiFileContentHeaderTitle] =
    useState(null);
  const [ediFileContentBody, setEdiFileContentBody] = useState(null);
  const [ediFileContentFormatStatus, setEdiFileContentFormatStatus] =
    useState(false);
  const [ediFileContentModalVisible, setEdiFileContentModalVisible] =
    useState(false);
  const [defaultRegion, setDefaultRegion] = useState([]);
  const dispatch = useDispatch();
  const { getRegions } = bindActionCreators(actionCreators, dispatch);

  const regions = useSelector((state) => {
    const data = state?.orderReducer?.regions;
    const sortedData = sortData("regionNameAbbr", data);
    return sortedData;
  });
  appInsights.trackPageView({ name: "Credit Invoice Summary screen visited" });
  const InvoiceCreditPercentageFilterDropdownOptions = [
    { label: "25%", value: 25 },
    { label: "50%", value: 50 },
    { label: "100%", value: 100 },
    { label: "125%", value: 125 }
  ];

  const initialValues = useMemo(
    () => ({
      invoiceNumber: "",
      creditPercentage: null,
      region: null,
      creditInvoiceStartDate: new Date(),
      creditInvoiceEndDate: new Date()
    }),
    []
  );

  useEffect(() => {
    getRegions();
  }, []);

  const validationSchema = Yup.object()
    .shape({
      creditInvoiceStartDate: Yup.date()
        .nullable()
        .typeError("Invalid start date"),

      creditInvoiceEndDate: Yup.date().nullable().typeError("Invalid end date")
    })
    .test(
      "dates-required-together",
      "Start and End Date are required together",
      function (values) {
        const { creditInvoiceStartDate, creditInvoiceEndDate } = values;

        const startSelected = !!creditInvoiceStartDate;
        const endSelected = !!creditInvoiceEndDate;

        if (
          (startSelected && !endSelected) ||
          (!startSelected && endSelected)
        ) {
          return this.createError({
            path: !startSelected
              ? "creditInvoiceStartDate"
              : "creditInvoiceEndDate",
            message: "Both Start and End Date must be selected together"
          });
        }

        return true;
      }
    );

  function formatDate(date) {
    var d = new Date(date),
      month = "" + (d.getMonth() + 1),
      day = "" + d.getDate(),
      year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const toggleRows = () => {
    if (allExpanded) {
      setExpandedRows(null);
      setAllExpanded(false);
    } else {
      let newExpandedRows = {};
      invoiceDetails.forEach((p) => (newExpandedRows[`${p?.id}`] = true));
      setExpandedRows(newExpandedRows);
      setAllExpanded(true);
    }
  };

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-1 pl-5 ml-5" style={{ width: "30%", minWidth: "5rem" }}>
        <DataTable
          value={data?.lineItems}
          paginatorTemplate={constants?.paginatorTemplate}
          paginator={constants?.paginator}
          rows={constants?.defaultRows}
          rowsPerPageOptions={constants?.rowsPerPageOptions}
          currentPageReportTemplate={constants?.currentPageReportTemplate}
          emptyMessage={constants?.emptyMessage}
        >
          {rowExpansionCreditInvoicesSummaryLineItemColumns.map(
            (col, index) => (
              <Column
                key={index}
                field={col?.field}
                header={col?.header}
                sortable={col?.sort}
                style={{ minWidth: col?.minWidth }}
                body={col?.value}
              />
            )
          )}
        </DataTable>
      </div>
    );
  };
  useEffect(() => {
    if (regions.length == 0) return;
    if (userData?.regionId != null) {
      let filteredRegion = regions?.filter(
        (item) => item?.regionId == userData?.regionId
      );
      formRef.current.setFieldValue("region", filteredRegion[0]);
      setDefaultRegion(filteredRegion[0]);
      fetchInvoiceDetails({ ...initialValues, region: filteredRegion[0] });
    } else {
      formRef.current.setFieldValue("region", regions[0]);
      fetchInvoiceDetails({ ...initialValues, region: regions[0] });
    }
  }, [regions]);
  const fetchInvoiceDetails = async (formBody) => {
    try {
      setShowSpinner(true);
      formBody = {
        invoiceNumber: formBody?.invoiceNumber ? formBody?.invoiceNumber : null,
        creditPercentage: formBody?.creditPercentage || null,
        StartDate: formBody?.creditInvoiceStartDate
          ? formatDate(formBody?.creditInvoiceStartDate)
          : null,
        endDate: formBody?.creditInvoiceEndDate
          ? formatDate(formBody?.creditInvoiceEndDate)
          : null,
        region: formBody?.region?.regionNameAbbr || null
      };
      let result = await getInvoiceDetails(formBody);

      setInvoiceDetails(result);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setInvoiceDetails([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error?.message,
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while fetching credit invoice summary"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
    setShowSpinner(false);
  };

  const formatEdiFileContent = () => {
    if (ediFileContentBody && !ediFileContentFormatStatus) {
      const formattedEdiFileContent = ediFileContentBody
        ?.split("~")
        .join("~\n");
      setEdiFileContentBody(formattedEdiFileContent);
      setEdiFileContentFormatStatus(true);
    }
  };

  const ediFileContent = async (ediFileContentObj) => {
    let response;
    try {
      setShowSpinner(true);
      response = await getEdi894FileContent(ediFileContentObj);
      if (response?.length == 0 || response?.fileName == null)
        throw Error("Failed to fetch file");
      setEdiFileContentHeaderTitle(response?.fileName);
      setEdiFileContentBody(response?.content);
      setShowSpinner(false);
      setEdiFileContentModalVisible(true);
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "File Content Request Failed",
        detail: error?.message,
        life: 3000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while Edi 894 file content"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }

    if (response?.statusCode && response?.statusCode !== 200) {
      setEdiFileContentModalVisible(false);
      toast?.current?.show({
        severity: "error",
        summary: "File Content Request Failed",
        detail: response?.result,
        life: 3000
      });
    }
  };

  const ediFileContentModalHeader = (
    <div className="flex justify-content-between flex-wrap">
      <div className="flex align-items-center justify-content-center">
        <span className="font-bold white-space-nowrap text-base">
          {ediFileContentHeaderTitle}
        </span>
      </div>
      {/* <div className="flex align-items-center justify-content-center">
        <Button
          label="Format"
          icon="pi pi-file-edit"
          onClick={() => formatEdiFileContent()}
          style={{ marginRight: "1rem" }}
          autoFocus
          disabled={!ediFileContentBody}
        />
      </div> */}
    </div>
  );
  const ediFileNameFileContentFormat = (rowData) => {
    return (
      <Button
        text
        onClick={() => ediFileContent({ id: rowData?.id })}
        style={{ color: "#22c55e", textDecoration: "none" }}
        className="white-space-nowrap p-0"
      >
        {rowData?.edI894FileName}
      </Button>
    );
  };

  const onHideEdiFileContentModal = () => {
    setEdiFileContentFormatStatus(false);
    setEdiFileContentModalVisible(false);
  };
  const errorBodyTemplate = (rowData) => {
    return rowData?.errors?.join(", ");
  };

  return (
    <>
      {showSpinner && <Spinner />}
      <Dialog
        visible={ediFileContentModalVisible}
        modal
        header={ediFileContentModalHeader}
        style={{ width: "70vw" }}
        breakpoints={{ "1024px": "75vw", "641px": "100vw" }}
        onHide={onHideEdiFileContentModal}
        maximizable
      >
        <pre style={{ whiteSpace: "pre-wrap" }}>{ediFileContentBody}</pre>
      </Dialog>

      <br></br>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          setCurrentPage(0);
          setCurrentPageRows(10);
          setShowSpinner(true);
          fetchInvoiceDetails(values);
        }}
        innerRef={formRef}
      >
        {({
          values,
          errors,
          touched,
          setFieldValue,
          handleSubmit,
          handleChange
        }) => (
          <Form onSubmit={handleSubmit} ref={formRef}>
            <div className="grid">
              <div className="col-12 md:col-2">
                <div className={styles?.formLabelContainer}>
                  <label
                    htmlFor="region"
                    style={{ marginBottom: "0.5rem", display: "block" }}
                  >
                    <span className={styles?.formLabel}>Region</span>
                  </label>
                </div>
                <Dropdown
                  id="region"
                  placeholder="Region"
                  value={values.region}
                  onChange={(e) => setFieldValue("region", e?.value)}
                  options={regions}
                  filter
                  optionLabel="regionNameAbbr"
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>
              <div className="col-12 md:col-2">
                <div className={styles?.formLabelContainer}>
                  <label
                    htmlFor="creditPercentage"
                    style={{ marginBottom: "0.5rem", display: "block" }}
                  >
                    <span className={styles?.formLabel}>Credit (%)</span>
                  </label>
                </div>
                <Dropdown
                  id="creditPercentage"
                  placeholder="Credit (%)"
                  options={InvoiceCreditPercentageFilterDropdownOptions}
                  value={values.creditPercentage}
                  onChange={(e) => setFieldValue("creditPercentage", e?.value)}
                  className={`${styles.dropdown} ${styles.formControl}`}
                />
              </div>
              <div className="col-12 md:col-2">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="invoiceNumber">
                    <span className={styles.formLabel}>Invoice Number</span>
                  </label>
                </div>
                <div className="p-inputgroup flex-1">
                  <span className="p-inputgroup-addon">
                    <i className="pi pi-search"></i>
                  </span>
                  <InputText
                    id="invoiceNumber"
                    placeholder="Invoice Number"
                    value={values?.invoiceNumber}
                    onChange={handleChange}
                    className={`${styles.inputField} ${styles.formControl} ${styles.noBorder}`}
                  />
                </div>
              </div>
              <div className="col-12 md:col-3">
                <div className={styles.formLabelContainer}>
                  <label htmlFor="deliveryStartDate">
                    <span className={styles.formLabel}>
                      Invoice Processed Date Range
                    </span>
                  </label>
                </div>
                <Calendar
                  id="creditInvoiceStartDate"
                  value={values?.creditInvoiceStartDate}
                  onChange={handleChange}
                  disabled={disableFields}
                  placeholder="Start Date"
                  maxDate={formRef?.current?.values?.creditInvoiceEndDate}
                />
                {touched.creditInvoiceStartDate &&
                  errors.creditInvoiceStartDate && (
                    <small className="p-error">
                      {errors.creditInvoiceStartDate}
                    </small>
                  )}
              </div>
              <div className="col-12 md:col-3">
                <div className={styles.formLabelContainer}></div>
                <Calendar
                  id="creditInvoiceEndDate"
                  value={values?.creditInvoiceEndDate}
                  onChange={handleChange}
                  disabled={disableFields}
                  placeholder="End Date"
                  minDate={formRef?.current?.values?.creditInvoiceStartDate}
                />
                {touched.creditInvoiceEndDate &&
                  errors.creditInvoiceEndDate && (
                    <small className="p-error">
                      {errors.creditInvoiceEndDate}
                    </small>
                  )}
              </div>
            </div>

            <div
              className="flex justify-content-end flex-wrap gap-3"
              style={{ marginTop: "1rem", marginBottom: "0.3rem" }}
            >
              <div className="flex align-items-center justify-content-center">
                <div className={styles?.formLabelContainer}></div>
                <Button
                  label="Search"
                  type="submit"
                  size="medium"
                  className={styles?.primaryBtn}
                />
              </div>
              <div className="flex align-items-center justify-content-center">
                <div className={styles?.formLabelContainer}></div>
                <Button
                  label="Reset"
                  type="button"
                  size="medium"
                  onClick={(e) => {
                    formRef.current.setValues({
                      invoiceNumber: "",
                      creditPercentage: null,
                      creditInvoiceStartDate: new Date(),
                      creditInvoiceEndDate: new Date(),
                      region: defaultRegion ? defaultRegion : regions[0]
                    });
                    setInvoiceDetails([]);
                    setDisableFields(false);
                    fetchInvoiceDetails({ ...initialValues, region: defaultRegion ? defaultRegion : regions[0] });
                  }}
                  className={styles.resetBtn}
                />
              </div>
            </div>
            <div className="flex justify-content-end ">
              <div className={styles.recordLimit}>
                <span><i className={`${styles.recordLimit} pi pi-info-circle p-0`} style={{ fontSize: '10px', marginBottom: "0.5rem" }}></i> Fetching latest 5000 records only</span>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <br></br>

      <DataTable
        value={invoiceDetails}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        first={currentPage}
        rows={currentPageRows}
        onPage={(e) => { setCurrentPage(e.first); setCurrentPageRows(e?.rows) }}
        paginator={constants?.paginator}
        rowsPerPageOptions={constants?.rowsPerPageOptions}
        paginatorTemplate={constants?.paginatorTemplate}
        currentPageReportTemplate={constants?.currentPageReportTemplate}
        emptyMessage={constants?.emptyMessage}
        dataKey="id"
        scrollHeight="30vw"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
      >
        <Column
          header={
            <Button
              className={allExpanded ? "pi pi-angle-down" : "pi pi-angle-right"}
              style={{
                fontSize: "1.3rem",
                color: "#6c757d",
                backgroundColor: "#f8f9fa",
                border: "none",
                padding: "2px"
              }}
              onClick={toggleRows}
            ></Button>
          }
          style={{ width: "10px" }}
          expander={true}
        />

        {CreditInvoicesSummaryColumns?.map((col, index) => (
          <Column
            key={index}
            field={col?.field}
            header={col?.header}
            sortable={col?.sort}
            style={{ minWidth: col?.minWidth }}
            body={
              col?.columnType?.toLocaleLowerCase() === "fun"
                ? col?.columnDefination
                : col?.field?.toLocaleLowerCase() === "errors"
                  ? errorBodyTemplate
                  : col?.field.toLocaleLowerCase() === "edi894filename"
                    ? ediFileNameFileContentFormat
                    : col?.value
            }
          />
        ))}
      </DataTable>

      <Toast ref={toast} position="center" />
    </>
  );
}